import React, { useCallback } from 'react';
import PropTypes from "prop-types";

import mathlive from 'mathlive';
import "moment/locale/pt-br"

import ViewEditor from '../../../../../shared/ViewEditor';

import * as Styles from './styles';
import { useDarkMode } from '../../../../../../hooks/useDarkMode';


function Screen({warning, onClick}) {
  const isDarkMode = useDarkMode();

  const cuttingTextDescription = description => { 
    const maxLength = 80;

    description = description.replace(/(<([^>]+)>)/gi, "")

    const hasSpace = description.slice(0, maxLength).lastIndexOf(" ") !== -1;

    if (description.length > maxLength) {
      if (hasSpace) {
        return `${description.slice(
          0,
          description.slice(0, maxLength).lastIndexOf(" ")
        )}...`;
      }
      return `${description.slice(0, maxLength)}...`;
    }
    return description;
  };

  const cuttingTextDescriptionWithHTML = (htmlText) => {
    const maxLength = 80;
    
    if (!htmlText) {
      return 'Sem descrição';
    }
    
    // Check if text contains HTML tags
    const hasHTML = /<[^>]+>/.test(htmlText);
    
    if (!hasHTML) {
      // No HTML, use simple text truncation
      return cuttingTextDescription(htmlText);
    }
    
    // For HTML content, create a temporary element to parse
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;
    
    // Remove all class attributes but preserve style attributes (for colors)
    const removeClasses = (element) => {
      if (element.hasAttribute && element.hasAttribute('class')) {
        element.removeAttribute('class');
      }
      if (element.children) {
        Array.from(element.children).forEach(child => removeClasses(child));
      }
    };
    removeClasses(tempDiv);
    
    // Get text content for length calculation
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // If text is short enough, return cleaned HTML
    if (textContent.length <= maxLength) {
      return tempDiv.innerHTML;
    }
    
    // For longer HTML, we'll use a simpler approach:
    // Try to find a safe truncation point that preserves HTML structure
    // This is a simplified version - for production, consider a library like html-truncate
    const SHOW_TEXT = 4; // NodeFilter.SHOW_TEXT constant
    let currentLength = 0;
    const walker = document.createTreeWalker(
      tempDiv,
      SHOW_TEXT,
      null,
      false
    );
    
    let node = walker.nextNode();
    while (node) {
      const nodeLength = node.textContent.length;
      if (currentLength + nodeLength > maxLength) {
        const remaining = maxLength - currentLength;
        const hasSpace = node.textContent.slice(0, remaining).lastIndexOf(' ') !== -1;
        const cutPoint = hasSpace 
          ? node.textContent.slice(0, remaining).lastIndexOf(' ')
          : remaining;
        node.textContent = `${node.textContent.slice(0, cutPoint)}...`;
        // Remove all following text nodes
        let nextNode = walker.nextNode();
        while (nextNode) {
          nextNode.textContent = '';
          nextNode = walker.nextNode();
        }
        break;
      }
      currentLength += nodeLength;
      node = walker.nextNode();
    }
    
    return tempDiv.innerHTML;
  };

  const renderText = useCallback(text => {
    // Pattern to identify latex expression like {\infty}
    const latexPattern = /\\\w+({.*})?/g;
    const hasLatex = latexPattern.test(text);
    if (hasLatex) {
      return (
        <span
          style={{ marginLeft: 5 }}
          className="card-warning-text"
          dangerouslySetInnerHTML={{
            __html: mathlive.latexToMarkup(text)
          }}
        />
      );
    }

    // Preserve HTML structure and inline styles for ViewEditor
    const formattedData = cuttingTextDescriptionWithHTML(text || 'Sem descrição');
    return <ViewEditor data={formattedData} darkMode={isDarkMode} />;
  }, [isDarkMode]);

  return (
    <Styles.Card darkMode={isDarkMode}>
      <Styles.CardHeader>
        <Styles.CardTitle darkMode={isDarkMode}>{cuttingTextDescription(warning.name)}</Styles.CardTitle>
      </Styles.CardHeader>
      <Styles.CardDivision />
      <Styles.CardContent>
        <Styles.CardText darkMode={isDarkMode}>
          {renderText(warning.message)}
        </Styles.CardText>
        <Styles.CardFooter>
          <Styles.CardTime darkMode={isDarkMode} dateTime={warning.publishDate}>
            Publicado {cuttingTextDescription(warning.time)}
          </Styles.CardTime>
          <Styles.CardButton darkMode={isDarkMode} type='button' onClick={() => onClick(warning)}>
            Ver mais
          </Styles.CardButton>
        </Styles.CardFooter>
      </Styles.CardContent>
    </Styles.Card>
  );
}

Screen.propTypes = {
  warning: PropTypes.shape({
    name: PropTypes.string,
    message: PropTypes.string,
    publishDate: PropTypes.string,
    time: PropTypes.string
  }).isRequired,
  onClick: PropTypes.func
};

Screen.defaultProps = {
  onClick: () => {}
}

export default Screen;
